import React, { useState, useRef, useEffect } from 'react'; 
import { storage } from '../firebaseConfig'; // Import Firebase Storage configuration
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject, listAll } from "firebase/storage"; // Import Firebase Storage functions
import loadingGif from '../Assets/loading.gif';
import QuiltBuilderWithLayout from './QuiltBuilderWithLayout';
import { analytics, db } from '../firebaseConfig';
import { getAnalytics, logEvent } from "firebase/analytics";
import { collection, addDoc, onSnapshot, query, orderBy, deleteDoc } from 'firebase/firestore';
import QuiltEditor from './QuiltEditor'; // Import QuiltEditor component
import PaymentGateway from './PaymentGateway';
import { doc, getDocs, setDoc, getDoc, updateDoc } from "firebase/firestore"; // Firestore functions
import { useAuth } from '../components/AuthContext';
import logoSrc from '../Assets/favicon-32x32.png'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faDownload, faShareAlt, faRedo, faSync, faTrash, faPencilAlt, faArrowLeft, faArrowRight, faRuler, faTshirt, faMagic, faSpinner, faChevronLeft, faChevronRight, faThLarge, faExpand, faTimes, faCheck, faUndo } from '@fortawesome/free-solid-svg-icons';
import AILayouts from './AILayouts';
import QuilterOptions from './QuilterOptions';
import ShareModal from './ShareModal';
import { drawOnCanvas } from './canvasUtils';
import { handleDownloadClick, handleShareClick, confirmReset, softReset } from "./quiltActions";
import priceMapping from './priceMapping';
import TawkChat from './TawkChat';
import HotjarIntegration from './HotjarIntegration';
import ImageAdjustmentModal from './ImageAdjustmentModal';
import { API_ENDPOINTS } from '../config/apiConfig'; // Import API configuration
import AdvancedCropEditor from './AdvancedCropEditor';
import { useNavigate, useLocation } from 'react-router-dom';

// Import functions from the new modules
import { 
    handleDeleteImage as deleteImage, 
    saveCropSettings as processCropSettings,
    handleImageUploaded as processImageUploaded,
    waitForAllTilesToUpload as processTileUploads,
    calculateRequiredImages as calculateTileCount
} from './ImageProcessor';

import {
    getPriceForGridSize as calculatePrice,
    handleAIClick as processAIClick,
    handlePaymentProceed as processPaymentProceed,
    getAIButtonText
} from './AIHandler';

// Import session management functions from SessionManager
import {
    saveSessionData,
    restoreSessionData,
    convertFirestoreToMatrix,
    saveAILayouts  // Add this import
} from './SessionManager';

// Import functions from ResetHandler and QuiltValidation
import {
    resetFormFields,
    confirmSoftReset as softResetHandler,  // Rename the import to avoid collision
    updateFirestoreAfterSoftReset
} from './ResetHandler';

import {
    isQuiltDimensionsValid,
    handleDimensionsChange as processDimensionsChange
} from './QuiltValidation';

// Import the new Step1 component
import Step1 from './Step1';

// Import the new Step2 component
import Step2 from './Step2';

// Import the new Step4 component
import Step4 from './Step4';

// Import the new Step5 component
import Step5 from './Step5';

// Import the new Step3 component
import Step3 from './Step3';

// Import the new Step6 component
import Step6 from './Step6';

// Define whitelist emails as a constant outside the component to ensure it's not recreated on rerender
const WHITELISTED_EMAILS = [
    //"gptonly522@gmail.com",
    "beautgroup@gmail.com", 
    "hari765@gmail.com", 
    "lakshman52264@gmail.com",
    "gptonly522@gmail.com"
];

function QuiltBuilder() {
    // Add step management state - MOVED UP to prevent "Cannot access 'currentStep' before initialization" error
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 6; // Increase total steps to 6
    
    // Other state variables
    const [uploadedImages, setUploadedImages] = useState([]);
    const [gridSize, setGridSize] = useState(''); // Default no selection for grid size
    const [generated, setGenerated] = useState(false); // Flag to indicate when to generate quilt layout
    const [requiredImages, setRequiredImages] = useState(0); // Number of images required
    const [showWarning, setShowWarning] = useState(''); // State for controlling the warning visibility
    const fileInputRef = useRef(null); // For accessing the file input
    const [quiltImageUrl, setQuiltImageUrl] = useState('');  // New state to hold the quilt image URL
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false); // New state for loading
    const [previousLayoutType, setPreviousLayoutType] = useState(''); // Track the previous layout type
    // Add this state to store the existing session ID
    const [existingSessionId, setExistingSessionId] = useState(null);
    const quiltDisplayRef = useRef(null); // Reference for quilt display section
    const [rating, setRating] = useState(0); // User rating
    const [feedback, setFeedback] = useState(''); // User feedback
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
    const canvasRef = useRef(null); // Canvas reference
    const [isEditing, setIsEditing] = useState(false); // Toggle for edit mode
    const [quiltGrid, setQuiltGrid] = useState([]); // Grid layout for quilt tiles
    const [sessionId, setSessionId] = useState(null);
    const [isEditUnlocked, setIsEditUnlocked] = useState(false);
    const [generateDisabled, setGenerateDisabled] = useState(false);
    const { currentUser } = useAuth(); // Get currentUser from context
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [isResetLoading, setIsResetLoading] = useState(false);
    const [originalQuiltGrid, setOriginalQuiltGrid] = useState([]); // Original grid layout for revert
    const [isProcessingImages, setIsProcessingImages] = useState(false);
    const [processingError, setProcessingError] = useState(null);
    const [showThankYouMessage, setShowThankYouMessage] = useState(false);
    const [shareLink, setShareLink] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [aiSuggestions, setAISuggestions] = useState(null);
    const [loadingAI, setLoadingAI] = useState(false);
    const [aiError, setAIError] = useState(null);
    // Function to toggle the reset modal
    const openResetModal = () => setIsResetModalOpen(true);
    const closeResetModal = () => setIsResetModalOpen(false);
    const [aiGeneratedLayouts, setAIGeneratedLayouts] = useState([]); // Stores AI layouts

    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false); // Payment modal visibility
    const [isPaymentCompleted, setIsPaymentCompleted] = useState(false); // Payment status
    const [userCountry, setUserCountry] = useState(null); // Detected country
    const [priceInRupees, setPriceInRupees] = useState(null); // Price in INR
    const [isPremiumUser, setIsPremiumUser] = useState(false);
    const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(null);
    const [currentRotation, setCurrentRotation] = useState(0);
    const [isProcessingStarted, setIsProcessingStarted] = useState(false);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [currentCrop, setCurrentCrop] = useState(null);
    const [isSoftResetModalOpen, setIsSoftResetModalOpen] = useState(false);
    const [isAdvancedEditorOpen, setIsAdvancedEditorOpen] = useState(false);
    const [editingImageIndex, setEditingImageIndex] = useState(null);
    
    // Add new state for tile dimensions
    const [quiltDimensions, setQuiltDimensions] = useState({
        quiltWidth: null,
        quiltHeight: null,
        tileWidth: null,
        tileHeight: null,
        columns: 0,
        rows: 0,
        totalTiles: 0,
        tileWidthPixels: 300,
        tileHeightPixels: 300
    });

    const navigate = useNavigate();
    const location = useLocation();

// Update saveSessionData to use the imported function
const handleSaveSessionData = async () => {
    if (!currentUser) {
        console.log("No current user, cannot save session");
        return;
    }
    
    try {
        const sessionRef = doc(db, 'sessions', currentUser.uid);
        await setDoc(sessionRef, {
            uploadedImages: uploadedImages,
            gridSize: gridSize,
            quiltImageUrl: quiltImageUrl,
            quiltGrid: quiltGrid,
            generated: generated,
            generateDisabled: generateDisabled,
            aiGeneratedLayouts: aiGeneratedLayouts,
            quiltDimensions: quiltDimensions,
            lastUpdated: new Date()
        }, { merge: true }); // merge: true ensures we don't overwrite existing data
        
        console.log("Session data saved successfully");
    } catch (error) {
        console.error("Error saving session data:", error);
    }
};

// Add a new effect to save quilt dimensions when they change or step changes
useEffect(() => {
    // Only save if we have valid dimensions and a user
    if (currentUser && quiltDimensions && quiltDimensions.quiltWidth && 
        quiltDimensions.quiltHeight && quiltDimensions.tileWidth && 
        quiltDimensions.tileHeight && gridSize) {
        
        console.log("Saving quilt dimensions to Firestore:", quiltDimensions);
        
        // Use setDoc with merge option instead of updateDoc to handle non-existent documents
        const sessionRef = doc(db, 'sessions', currentUser.uid);
        setDoc(sessionRef, { 
            quiltDimensions: quiltDimensions,
            gridSize: gridSize,
            lastUpdated: new Date()
        }, { merge: true })  // merge: true ensures we don't overwrite existing data
        .catch(error => {
            console.error("Error saving quilt dimensions to Firestore:", error);
        });
    }
}, [quiltDimensions, currentStep, gridSize, currentUser, db]);

// Restore session data on component mount
useEffect(() => {
    if (currentUser) {
        restoreSessionData(
            currentUser,
            db,
            setGridSize,
            setUploadedImages,
            setQuiltImageUrl,
            setQuiltGrid,
            setGenerated,
            setGenerateDisabled,
            setFeedbackSubmitted,
            setAIGeneratedLayouts,
            setQuiltDimensions,
            setRequiredImages,
            calculateTileCount,
            setLayoutSuggestions  // Make sure this is passed
        ).then((restoredData) => {
            // Store the restored data in sessionStorage for backup access
            try {
                sessionStorage.setItem('firestoreSessionData', JSON.stringify({
                    layoutSuggestions: restoredData.layoutSuggestions || [],
                    aiGeneratedLayouts: restoredData.aiGeneratedLayouts || []
                }));
            } catch (err) {
                console.error("Error storing session data in sessionStorage:", err);
            }

            // Determine the appropriate step based on restored data
            if (restoredData.aiGeneratedLayouts && 
                Array.isArray(restoredData.aiGeneratedLayouts) && 
                restoredData.aiGeneratedLayouts.length > 0) {
                // If AI layouts exist, go to step 5
                setCurrentStep(5);
                console.log("Restored session with AI layouts, navigating to step 5");
            } else if (restoredData.layoutSuggestions && 
                       Array.isArray(restoredData.layoutSuggestions) && 
                       restoredData.layoutSuggestions.length > 0) {
                // If layout suggestions exist but no AI layouts, go to step 4
                setCurrentStep(4);
                console.log("Restored session with layout suggestions, navigating to step 4");
            } else if (restoredData.quiltImageUrl) {
                // If quilt image exists but no AI layouts, go to step 3
                setCurrentStep(3);
                console.log("Restored session with quilt image, navigating to step 3");
            } else if (restoredData.uploadedImages && restoredData.uploadedImages.length > 0) {
                // If images are uploaded but no quilt yet, go to step 2
                setCurrentStep(2);
                console.log("Restored session with uploaded images, navigating to step 2");
            } else {
                // Default to Step 1
                setCurrentStep(1);
                console.log("No session data found, starting at step 1");
            }
        });
    }
}, [currentUser]);

// Restore saved state from session storage
useEffect(() => {
    const savedState = JSON.parse(sessionStorage.getItem("quiltState"));
    if (savedState) {
        console.log("Restoring state from sessionStorage:", savedState);

        setGridSize(savedState.gridSize || '');
        setUploadedImages(savedState.uploadedImages || []);
        setQuiltImageUrl(savedState.quiltImageUrl || '');
        setQuiltGrid(savedState.quiltGrid || []);
        setIsEditUnlocked(savedState.isEditUnlocked || false);
        
        // Also restore generated and generateDisabled states
        const hasQuiltImage = !!savedState.quiltImageUrl;
        setGenerated(savedState.generated || hasQuiltImage); // Use saved value or derive from quiltImageUrl
        setGenerateDisabled(savedState.generateDisabled || hasQuiltImage); // Use saved value or derive from quiltImageUrl
        
        // Restore AI layouts from session storage
        if (savedState.aiGeneratedLayouts) {
            setAIGeneratedLayouts(savedState.aiGeneratedLayouts);
        }

        // Determine the appropriate step based on restored data
        if (savedState.aiGeneratedLayouts && 
            Array.isArray(savedState.aiGeneratedLayouts) && 
            savedState.aiGeneratedLayouts.length > 0) {
            // If AI layouts exist, go to step 5
            setCurrentStep(5);
            console.log("AI layouts found in session, navigating to step 5");
        } else if (savedState.quiltImageUrl) {
            // If quilt image exists but no AI layouts, go to step 3
            setCurrentStep(3);
        } else if (savedState.uploadedImages && savedState.uploadedImages.length > 0) {
            // If images are uploaded but no quilt yet, go to step 2
            setCurrentStep(2);
        } else {
            // Default to Step 1
            setCurrentStep(1);
        }

        console.log("isEditUnlocked after restoration:", savedState.isEditUnlocked);
        sessionStorage.removeItem("quiltState");
    }
}, []);
useEffect(() => {
    const checkInitialPaymentStatus = async () => {
        if (!currentUser) return;

        try {
            const paymentsCollectionRef = collection(db, `customers/${currentUser.uid}/payments`);
            const querySnapshot = await getDocs(paymentsCollectionRef);

            if (!querySnapshot.empty) {
                const paymentDoc = querySnapshot.docs[0];
                const paymentData = paymentDoc.data();
                setIsPremiumUser(paymentData.status === "succeeded");
            }
        } catch (error) {
            console.error("Error checking initial payment status:", error);
        }
    };

    checkInitialPaymentStatus();
}, [currentUser]);

// Save session data whenever relevant states change
useEffect(() => {
    if (generated) {
        handleSaveSessionData();
    }
}, [uploadedImages, gridSize, quiltGrid, aiGeneratedLayouts]);

// Set session ID when user is available
useEffect(() => {
    if (currentUser && !sessionId) {
        setSessionId(currentUser.uid);
        setExistingSessionId(currentUser.uid); // Synchronize with existingSessionId
    }
}, [currentUser, sessionId]);

// Scroll to quilt image when it's available
useEffect(() => {
    if (quiltImageUrl && !loading) {
        setTimeout(() => {
            scrollToQuilt();
        }, 100); // Short delay to ensure rendering is complete
    }
}, [quiltImageUrl, loading]);

// Scroll function
const scrollToQuilt = () => {
    if (quiltDisplayRef.current) {
        const offset = -50; // Adjust offset if needed
        const topPosition = quiltDisplayRef.current.getBoundingClientRect().top + window.scrollY + offset;
        window.scrollTo({ top: topPosition, behavior: 'smooth' });
    }
};

// Scroll down during loading
useEffect(() => {
    if (loading) {
        scrollToQuilt();
    }
}, [loading]);

// Scroll to top when component loads
useEffect(() => {
    window.scrollTo(0, 0);
}, []);

// Update canvas whenever the quilt grid changes
useEffect(() => {
    console.log("useEffect triggered: quiltGrid =", quiltGrid);
    
    // Ensure we have a valid canvas reference and grid before drawing
    if (quiltGrid.length > 0 && canvasRef.current && gridSize) {
        // Wrap in setTimeout to ensure the canvas is fully rendered
        setTimeout(() => {
            try {
                // Always use enhanced tile size for basic layout (step 3)
                // This ensures consistency whether loading from session or navigating to step 3
                const isBasicLayout = currentStep === 3;
                
                if (isBasicLayout) {
                    console.log("Drawing basic layout with enhanced tile size");
                    drawOnCanvas(canvasRef, quiltGrid, gridSize, logoSrc, true);
                } else {
                    drawOnCanvas(canvasRef, quiltGrid, gridSize, logoSrc);
                }
            } catch (error) {
                console.error("Error drawing on canvas:", error);
            }
        }, 100); // Small delay to ensure canvas is ready
    }
}, [quiltGrid, gridSize, logoSrc, currentStep]);

// Add an effect to fix canvas rendering issues when restoring session
useEffect(() => {
    // If we have quilt grid data but the canvas reference might not be ready,
    // add a delayed re-render attempt with multiple retries
    if (quiltGrid && quiltGrid.length > 0 && gridSize) {
        // Try to draw at different intervals to ensure it eventually succeeds
        const retryTimes = [300, 600, 1000]; // Try after 300ms, 600ms, and 1000ms
        
        const retryTimers = retryTimes.map(delay => {
            return setTimeout(() => {
                if (canvasRef.current) {
                    try {
                        console.log(`Retry drawing canvas at ${delay}ms`);
                        
                        // Always use enhanced tile size for basic layout (step 3)
                        // This ensures consistency whether loading from session or navigating to step 3
                        const isBasicLayout = currentStep === 3;
                        
                        if (isBasicLayout) {
                            console.log("Drawing basic layout with enhanced tile size");
                            drawOnCanvas(canvasRef, quiltGrid, gridSize, logoSrc, true);
                        } else {
                            drawOnCanvas(canvasRef, quiltGrid, gridSize, logoSrc);
                        }
                    } catch (error) {
                        console.error(`Error drawing on canvas during retry at ${delay}ms:`, error);
                    }
                }
            }, delay);
        });
        
        // Clean up all timers on unmount
        return () => {
            retryTimers.forEach(timer => clearTimeout(timer));
        };
    }
}, [quiltImageUrl, gridSize, logoSrc, currentStep, quiltGrid]); // Added quiltGrid to dependencies

// Also add an additional useEffect to handle window resize events
useEffect(() => {
    // Redraw canvas when window is resized to ensure proper scaling
    const handleResize = () => {
        if (quiltGrid.length > 0 && canvasRef.current && gridSize) {
            try {
                // Use the same logic as the main useEffect for consistency
                const isBasicLayout = currentStep === 3;
                drawOnCanvas(canvasRef, quiltGrid, gridSize, logoSrc, isBasicLayout);
            } catch (error) {
                console.error("Error drawing on canvas during resize:", error);
            }
        }
    };
    
    window.addEventListener('resize', handleResize);
    
    // Clean up event listener on unmount
    return () => {
        window.removeEventListener('resize', handleResize);
    };
}, [quiltGrid, gridSize, logoSrc, currentStep]);

// Add an effect to fix canvas rendering issues when restoring session
useEffect(() => {
    // If we have quilt grid data but the canvas reference might not be ready,
    // add a delayed re-render attempt with multiple retries
    if (quiltGrid && quiltGrid.length > 0 && gridSize) {
        // Try to draw at different intervals to ensure it eventually succeeds
        const retryTimes = [300, 600, 1000]; // Try after 300ms, 600ms, and 1000ms
        
        const retryTimers = retryTimes.map(delay => {
            return setTimeout(() => {
                if (canvasRef.current) {
                    try {
                        console.log(`Retry drawing canvas at ${delay}ms`);
                        drawOnCanvas(canvasRef, quiltGrid, gridSize, logoSrc);
                    } catch (error) {
                        console.error(`Error drawing on canvas during retry at ${delay}ms:`, error);
                    }
                }
            }, delay);
        });
        
        // Clean up all timers on unmount
        return () => {
            retryTimers.forEach(timer => clearTimeout(timer));
        };
    }
}, [quiltImageUrl]); // Only run when quiltImageUrl changes, which indicates a new quilt was generated

useEffect(() => {
    // Force reset input fields when user confirms reset
    if (isResetModalOpen === false && isResetLoading === false) {
        // This runs after the reset modal closes and loading is complete
        // Helps ensure QuiltSetup inputs are properly reset
        const resetInputFields = setTimeout(() => {
            const inputFields = document.querySelectorAll('input[type="text"], input[type="number"]');
            inputFields.forEach(input => {
                if (input.name && input.name.toLowerCase().includes('quilt')) {
                    input.value = '';
                }
            });
        }, 100);
        
        return () => clearTimeout(resetInputFields);
    }
}, [isResetModalOpen, isResetLoading]);



    const waitForAllTilesToUpload = async () => {
        return await processTileUploads(currentUser, db, requiredImages);
    };




        // Function to toggle edit mode
        const handleEditClick = () => {
            console.log("Edit layout button clicked");
            setOriginalQuiltGrid([...quiltGrid]);
            setIsEditing(true); // Enter edit mode
        };
    
// Update handleSaveChanges function to ensure image controls remain available
const handleSaveChanges = (updatedGrid) => {
    console.log("Saving changes with updatedGrid:", updatedGrid);
    
    // Make sure all tiles remain processed so the edit/delete buttons stay visible
    const processedGrid = updatedGrid.map(tile => ({
        ...tile,
        processed: true
    }));
    
    // First update state to exit edit mode
    setIsEditing(false);
    
    // Then update the grid - do this separately to ensure UI updates first
    setQuiltGrid(processedGrid);
    
    // Set a longer timeout to ensure state updates have propagated before trying to redraw
    setTimeout(() => {
        try {
            // If we're in Step 3, force the Step3 component to redraw its canvas
            if (currentStep === 3) {
                console.log("Step 3 detected, looking for step3-canvas...");
                const step3Canvas = document.getElementById('step3-canvas');
                if (step3Canvas) {
                    console.log("Redrawing Step 3 canvas");
                    drawOnCanvas({ current: step3Canvas }, processedGrid, gridSize, logoSrc, true);
                }
            } 
            // Otherwise use the main canvas reference
            else if (canvasRef.current) {
                console.log("Redrawing main canvas");
                drawOnCanvas(canvasRef, processedGrid, gridSize, logoSrc);
            }
        } catch (error) {
            console.error("Error drawing on canvas after editing:", error);
        }
    }, 200); // Increased timeout to ensure DOM is updated
}
// Update handleCancelEdit to also preserve processed status
const handleCancelEdit = () => {
    setQuiltGrid([...originalQuiltGrid]); // Revert to original layout
    setIsEditing(false); // Exit edit mode without saving
    
    // Ensure the tiles remain processed
    setUploadedImages(prevImages => 
        prevImages.map(img => ({
            ...img,
            processed: true
        }))
    );
};


// Update calculateRequiredImages to handle larger custom grid sizes
const calculateRequiredImages = (gridSize) => {
    return calculateTileCount(gridSize);
};


    // Function to calculate required images based on grid size
    const handleGridSizeChange = (newGridSize) => {
        // If images are already uploaded, don't allow grid size changes


        // Validate the grid size format
        if (!newGridSize || !newGridSize.includes('x')) {
            setShowWarning('Invalid grid size format.');
            return;
        }
        
        // Check for minimum grid size
        const [columns, rows] = newGridSize.split('x').map(Number);
        if (columns < 4 || rows < 4) {
            setShowWarning('Grid size cannot be smaller than 4x4.');
            return;
        }

        setGridSize(newGridSize);
        const calculatedImages = calculateRequiredImages(newGridSize);
        setRequiredImages(calculatedImages);

        logEvent(analytics, 'select_grid_size', { grid_size: newGridSize });

        // Allow user to upload more images if new grid size requires more
        if (uploadedImages.length < calculatedImages) {
            setShowWarning(`Please upload ${calculatedImages - uploadedImages.length} more T-shirts for the ${newGridSize} grid.`);
        } else if (uploadedImages.length === calculatedImages) {
            setShowWarning(''); // No warning if the exact number of images is already uploaded
        } else {
            setShowWarning(`You have uploaded more than required. You can only upload ${calculatedImages} T-shirts for the ${newGridSize} grid.`);
        }

        setGenerated(false); // Reset the generation flag if the grid size is changed
    };


    // Handle image upload click
    const handleUploadClick = (e) => {
        if (!gridSize ) {
            e.preventDefault(); // Prevent the file picker from opening
            setShowWarning('Please select grid size before uploading images.');
        } else if (uploadedImages.length >= requiredImages) {
            e.preventDefault(); // Prevent file picker if user has uploaded the maximum number of images
            setShowWarning(`You can only upload ${requiredImages} T-shirts for the ${gridSize} grid.`);
        } else {
            setShowWarning(''); // Hide warning if everything is selected
        }
    };

    const handleGenerateClick = async () => {
        if (uploadedImages.length < requiredImages) {
            setShowWarning(`Please upload exactly ${requiredImages} T-shirts for the ${gridSize} grid before generating.`);
        } else if (!gridSize) {
            setShowWarning('Please select grid size before generating.');
        } else if (isProcessingImages || processingError) {
            setShowWarning(processingError ? processingError : 'Processing images, please wait...');
        } else {
            setGenerated(true);
            setLoading(true); // Start loading state
            setShowWarning('');
            setGenerateDisabled(true); // Disable Generate button
        
            try {
                // Wait for all tiles to be uploaded before generating the quilt
                const allTilesUploaded = await waitForAllTilesToUpload(currentUser, db, requiredImages);
                if (!allTilesUploaded) {
                    setShowWarning("Not all images have been processed. Please try again.");
                    setLoading(false);
                    return;
                }
                
                // Create an array of current image URLs to ensure deleted images are filtered out
                const currentImageUrls = uploadedImages
                    .filter(img => img && img.url) // Ensure we only include valid images
                    .map(img => {
                        // Use the most appropriate URL for synchronization
                        // Original URLs are preferred for accurate matching
                        return img.originalUrl || img.originalTshirtUrl || img.tshirtUrl || img.url;
                    });
                
                console.log("Current image URLs being sent:", currentImageUrls.length);
        
                // Include image adjustments and tile dimensions in the payload
                const payload = {
                    session_id: currentUser.uid,  // Use the userId as sessionId
                    grid_size: gridSize,
                    layout_type: "Random",
                    // Add image adjustments to the payload
                    image_adjustments: uploadedImages.map(img => ({
                        url: img.url,
                        rotation: img.rotation || 0,
                        adjusted: img.adjusted || false,
                        crop: img.crop || null
                    })),
                    // Add tile dimensions to the payload
                    tile_dimensions: {
                        width: quiltDimensions.tileWidthPixels,
                        height: quiltDimensions.tileHeightPixels
                    },
                    // Add the array of current image URLs
                    current_image_urls: currentImageUrls
                };
        
                const apiRequest = fetch(API_ENDPOINTS.GENERATE_QUILT, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                }).then(response => response.json());
        
                const delay = new Promise(resolve => setTimeout(resolve, 3000));
                const [result] = await Promise.all([apiRequest, delay]);
        
                if (result && result.quilt_image_url) {
                    console.log('Quilt generated successfully:', result);
                    setQuiltImageUrl(result.quilt_image_url);
                    setQuiltGrid(result.tile_urls);
                    //await saveSessionData();
                } else {
                    const errorMessage = result.error || 'An unexpected error occurred while generating the quilt. Please reset and try again.';
                    console.error('Failed to generate quilt:', errorMessage);
                    setShowWarning(`Error generating quilt: ${errorMessage}`);
                }
            } catch (error) {
                console.error('Error during image upload or API request:', error);
                setShowWarning(`Error: ${error.message}`);
            } finally {
                setLoading(false); // End loading state after at least 3 seconds
            }
        }
    };

const handleDeleteImage = async (index) => {
    // When deleting images after a layout is generated, mark that a regeneration is needed
    if (quiltImageUrl) {
        setGenerateDisabled(false);
        setShowWarning("You've modified your images. Please generate a new layout when you're ready.");
    }
    
    await deleteImage(index, uploadedImages, setUploadedImages, currentUser, storage, db, setShowWarning);
};


    // Handle clicking the disabled generate button (fake button overlay)
    const handleDisabledGenerateClick = () => {
        if (!gridSize) {
            setShowWarning('Please select grid size before generating.');
        } else if (uploadedImages.length < requiredImages) {
            setShowWarning(`Please upload exactly ${requiredImages} T-shirts for the ${gridSize} grid before generating.`);
        }
    };

const handleAIClick = async () => {
    const result = await processAIClick({
        currentUser,
        db,
        sessionId,
        gridSize,
        setLoadingAI,
        setAIError,
        navigate
    });
    
    console.log("AI Click result:", result);
    
    // If payment is needed, show the payment modal and return this info
    if (result && result.showPaymentModal) {
        console.log("Opening payment modal due to result.showPaymentModal=true");
        setIsPaymentModalOpen(true); // Set payment modal to visible
        
        // Wait briefly to ensure state update before returning
        await new Promise(resolve => setTimeout(resolve, 50));
        
        return {
            success: false,
            showPaymentModal: true
        };
    } 
    
    // If successful generation, proceed with other logic
    if (result.success) {
        setIsPaymentCompleted(true);
        
        // Set a flag in sessionStorage to indicate we're coming from handleAIClick
        sessionStorage.setItem("fromAIClick", "true");
        
        // Load layout suggestions from Firestore
        try {
            if (currentUser && currentUser.uid) {
                const sessionRef = doc(db, 'sessions', currentUser.uid);
                const sessionSnap = await getDoc(sessionRef);
                
                if (sessionSnap.exists()) {
                    const sessionData = sessionSnap.data();
                    
                    // Use layoutSuggestions from Firestore if available
                    if (sessionData.layoutSuggestions && 
                        Array.isArray(sessionData.layoutSuggestions) && 
                        sessionData.layoutSuggestions.length > 0) {
                        
                        console.log("Loading layout suggestions from Firestore:", sessionData.layoutSuggestions);
                        setLayoutSuggestions(sessionData.layoutSuggestions);
                        
                        // Also store in sessionStorage for backup access
                        try {
                            sessionStorage.setItem('firestoreSessionData', JSON.stringify({
                                layoutSuggestions: sessionData.layoutSuggestions
                            }));
                        } catch (err) {
                            console.error("Error storing layoutSuggestions in sessionStorage:", err);
                        }
                        
                        // Move to edit suggestions step
                        setCurrentStep(4);
                        
                        // Clear the flag after a short delay to not interfere with future navigations
                        setTimeout(() => {
                            sessionStorage.removeItem("fromAIClick");
                        }, 1000);
                    } else {
                        console.warn("No layout suggestions found in Firestore session data");
                        setAIError("No layout suggestions were generated. Please try again.");
                        setLayoutSuggestions([]);
                    }
                }
                
                // Return successful result
                return {
                    success: true,
                    showPaymentModal: false
                };
            }
        } catch (error) {
            console.error("Error loading layout suggestions from Firestore:", error);
            setAIError(`Error: ${error.message}`);
            setLayoutSuggestions([]);
            sessionStorage.removeItem("fromAIClick"); // Clean up in case of error
            
            // Return error result
            return {
                success: false,
                showPaymentModal: false,
                error: error.message
            };
        }
    }
    
    // Return the original result if we don't have special handling for it
    return result;
};

    const getButtonText = () => {
        return getAIButtonText(loadingAI, isPremiumUser);
    };
    

const PaymentModal = ({ onClose, onProceed }) => {
    const [isRedirecting, setIsRedirecting] = useState(false);

    const handleProceed = async () => {
        setIsRedirecting(true);
        await onProceed();
    };
    
    const handleCancel = () => {
        // Return a canceled result when user clicks Cancel
        onClose({ canceled: true });
    };

    return (
        <div className="payment-modal fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-filter backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-auto overflow-y-auto max-h-[90vh]">
                <div className="p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-semibold" style={{ fontFamily: "Playfair Display" }}>
                        Complete Your Payment
                    </h2>
                    <p className="mt-2 text-gray-600 text-sm sm:text-base" style={{ fontFamily: "Playfair Display" }}>
                        To unlock the "Generate 5 AI-Enhanced Layouts" feature, please proceed with the payment. 
                        Once completed, you'll gain access to AI-powered layout generation tailored to your quilt.
                    </p>
                    <p className="mt-2 text-sm text-blue-600" style={{ fontFamily: "Playfair Display" }}>
                        Have a promotional code? You'll be able to apply it on the payment page.
                    </p>
                    <div className="mt-4 flex flex-col sm:flex-row sm:justify-end gap-3 sm:gap-4">
                        <button
                            className="w-full sm:w-auto px-4 py-2 bg-gray-200 rounded-md text-gray-700 hover:bg-gray-300 order-2 sm:order-1"
                            onClick={handleCancel}
                            disabled={isRedirecting}
                            style={{ fontFamily: "Playfair Display" }}
                        >
                            Cancel
                        </button>
                        <button
                            className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center justify-center order-1 sm:order-2"
                            onClick={handleProceed}
                            disabled={isRedirecting}
                            style={{ fontFamily: "Playfair Display" }}
                        >
                            {isRedirecting ? (
                                <>
                                    <svg
                                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                    Redirecting...
                                </>
                            ) : (
                                'Proceed to Payment'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

useEffect(() => {
    if (!gridSize) return;
    getPriceForGridSize(gridSize);
  }, [gridSize, userCountry]);
  
  



const handlePaymentProceed = async () => {
    return processPaymentProceed({
        currentUser, 
        db, 
        gridSize, 
        setAIError, 
        setIsPaymentModalOpen, 
        priceMapping
    });
};

useEffect(() => {
    const fetchUserCountry = async () => {
        try {
            const response = await fetch('https://ipapi.co/json/');
            const data = await response.json();

            if (data && data.country_code) {
                console.log(`User country: ${data.country_name} (${data.country_code})`);
                setUserCountry(data.country_code); // Store the country code (e.g., 'IN' for India)
            } else {
                console.warn('Country data not found. Defaulting to USD.');
                setUserCountry('US'); // Fallback to 'US'
            }
        } catch (error) {
            console.error('Error fetching country:', error);
            setUserCountry('US'); // Fallback to 'US' on error
        }
    };

    fetchUserCountry();
}, []);

// Function to calculate price for a given grid size
const getPriceForGridSize = (size) => {
    const { priceInRupees, priceFormatted } = calculatePrice(size, userCountry);
    setPriceInRupees(priceInRupees);
    return priceFormatted;
};


    // Function to open the adjustment modal for a specific image
    const handleAdjustImage = (index) => {
        if (index < 0 || index >= uploadedImages.length) {
            console.error("Invalid image index:", index);
            return;
        }
        
        // When adjusting images after a layout is generated, mark that a regeneration is needed
        if (quiltImageUrl) {
            setGenerateDisabled(false);
            setShowWarning("You've modified your images. Please generate a new layout when you're ready.");
        }
        
        const imageToEdit = uploadedImages[index];
        console.log("Opening editor for image:", imageToEdit);
        
        // Use data URL as the most reliable source, then fall back to other URLs
        // Add null checks to prevent "Cannot read properties of undefined" errors
        const originalImageUrl = imageToEdit ? (
            imageToEdit.dataUrl || 
            imageToEdit.originalTshirtUrl || 
            imageToEdit.originalUrl || 
            imageToEdit.url
        ) : null;
                                
        if (!originalImageUrl) {
            console.error("No valid URL found for image at index", index);
            setShowWarning("Error: Could not edit this image. Please try uploading it again.");
            return;
        }
        
        console.log("Using original image URL for editing:", 
            originalImageUrl.substring(0, 50) + (originalImageUrl.length > 50 ? '...' : ''));
        
        // Save current processing state before editing
        const wasProcessed = imageToEdit.processed;
        
        // Store the original state so we can restore it if cancelled
        setEditingImageIndex(index);
        
        // Store the processed state in the element for recovery if needed
        const updatedImages = [...uploadedImages];
        updatedImages[index] = {
            ...updatedImages[index],
            _previousProcessedState: wasProcessed // Store the previous state
        };
        setUploadedImages(updatedImages);
        
        // Now open the editor with the reliable image URL
        setIsAdvancedEditorOpen(true);
    };

    // Add this function to handle cancellation of the advanced editor
    const handleAdvancedEditorCancel = () => {
        if (editingImageIndex !== null && editingImageIndex >= 0 && editingImageIndex < uploadedImages.length) {
            // Restore the original processed state
            const updatedImages = [...uploadedImages];
            const previousState = updatedImages[editingImageIndex]._previousProcessedState;
            
            // If there was a previous processed state stored, restore it
            if (previousState !== undefined) {
                updatedImages[editingImageIndex] = {
                    ...updatedImages[editingImageIndex],
                    processed: previousState // Restore the previous processed state
                };
                
                // Remove the temporary state storage property
                delete updatedImages[editingImageIndex]._previousProcessedState;
                
                setUploadedImages(updatedImages);
                console.log(`Restored processed state (${previousState}) for image at index ${editingImageIndex}`);
            }
        }
        
        // Close the editor and reset the editing index
        setIsAdvancedEditorOpen(false);
        setEditingImageIndex(null);
    };

    // Function to save image rotation and crop after adjustment
    const handleSaveImageAdjustment = async (rotation, crop) => {
        if (currentImageIndex === null) return;

        try {
            console.log("Saving image adjustments:", { rotation, crop });
            
            // Get the current image
            const currentImage = uploadedImages[currentImageIndex];
            
            // Check if rotation or crop has actually changed
            const rotationChanged = rotation !== (currentImage.rotation || 0);
            
            // Determine if crop was explicitly changed by the user
            // If crop has default values or is identical to the current image's crop, consider it unchanged
            let cropChanged = false;
            const defaultCrop = { x: 10, y: 10, width: 80, height: 80 };
            const currentCropString = JSON.stringify(currentImage.crop || defaultCrop);
            const newCropString = JSON.stringify(crop || defaultCrop);
            cropChanged = newCropString !== currentCropString;
            
            console.log("Changes detected:", { rotationChanged, cropChanged });
            
            // If neither rotation nor crop changed, just close the modal without changes
            if (!rotationChanged && !cropChanged) {
                console.log("No changes detected, closing modal without modifications");
                setIsAdjustmentModalOpen(false);
                setCurrentImageIndex(null);
                return;
            }
            
            // Create a preview of the adjusted image
            const createPreview = async () => {
                try {
                    const img = new Image();
                    img.crossOrigin = "Anonymous";
                    
                    return new Promise((resolve) => {
                        img.onload = function() {
                            try {
                                const canvas = document.createElement('canvas');
                                const ctx = canvas.getContext('2d');
                                
                                // Set initial canvas dimensions
                                canvas.width = img.width;
                                canvas.height = img.height;
                                
                                // Clear canvas
                                ctx.clearRect(0, 0, canvas.width, canvas.height);
                                
                                // Apply rotation if it changed
                                if (rotationChanged) {
                                    // Calculate new dimensions for rotated image
                                    if (Math.abs(rotation) === 90 || Math.abs(rotation) === 270) {
                                        canvas.width = img.height;
                                        canvas.height = img.width;
                                    }
                                    
                                    // Translate and rotate
                                    ctx.translate(canvas.width / 2, canvas.height / 2);
                                    ctx.rotate((rotation * Math.PI) / 180);
                                    ctx.drawImage(img, -img.width / 2, -img.height / 2);
                                } else {
                                    // Draw without rotation
                                    ctx.drawImage(img, 0, 0);
                                }
                                
                                // Only apply crop if it was explicitly changed
                                if (cropChanged && crop) {
                                    // Get the cropped image data
                                    const cropX = (crop.x / 100) * canvas.width;
                                    const cropY = (crop.y / 100) * canvas.height;
                                    const cropWidth = (crop.width / 100) * canvas.width;
                                    const cropHeight = (crop.height / 100) * canvas.height;
                                    
                                    // Make sure crop dimensions are valid
                                    if (cropWidth > 0 && cropHeight > 0) {
                                        try {
                                            const imageData = ctx.getImageData(cropX, cropY, cropWidth, cropHeight);
                                            
                                            // Resize canvas to cropped size
                                            canvas.width = cropWidth;
                                            canvas.height = cropHeight;
                                            
                                            // Clear and put the cropped image data
                                            ctx.clearRect(0, 0, canvas.width, canvas.height);
                                            ctx.putImageData(imageData, 0, 0);
                                        } catch (cropError) {
                                            console.error("Error cropping image:", cropError);
                                        }
                                    }
                                }
                                
                                // Create data URL for preview
                                const previewUrl = canvas.toDataURL('image/jpeg');
                                resolve(previewUrl);
                            } catch (canvasError) {
                                console.error("Error creating canvas preview:", canvasError);
                                resolve(null);
                            }
                        };
                        
                        img.onerror = function() {
                            console.error("Error loading image for preview");
                            resolve(null);
                        };
                        
                        img.src = currentImage.url;
                    });
                } catch (error) {
                    console.error("Error in preview creation:", error);
                    return null;
                }
            };
            
            // Generate preview
            const previewUrl = await createPreview();
            
            // Update the image in state - only include changed properties
            const updatedImage = {
                ...currentImage,
                adjusted: true,
                processed: false,  // Mark as unprocessed to force reprocessing
                previewUrl: previewUrl,  // Save the preview URL
                adjustmentTimestamp: Date.now()
            };
            
            // Only update rotation if it changed
            if (rotationChanged) {
                updatedImage.rotation = rotation;
            }
            
            // Only update crop if it changed
            if (cropChanged) {
                updatedImage.crop = crop;
            }
            
            // Update state with the new image
            const updatedImages = [...uploadedImages];
            updatedImages[currentImageIndex] = updatedImage;
            
            console.log("Updated image with preview:", previewUrl ? "Preview generated" : "No preview");
            setUploadedImages(updatedImages);
            
            // Close the modal and reset current index
            setIsAdjustmentModalOpen(false);
            setCurrentImageIndex(null);
            
        } catch (error) {
            console.error("Error saving image adjustment:", error);
            setShowWarning("Failed to save image adjustment. Please try again.");
        }
    };

    // Function to cancel image adjustment
    const handleCancelAdjustment = () => {
        setIsAdjustmentModalOpen(false);
        setCurrentImageIndex(null);
    };

    // Function to toggle the soft reset modal
    const openSoftResetModal = () => setIsSoftResetModalOpen(true);
    const closeSoftResetModal = () => setIsSoftResetModalOpen(false);

    // Soft reset handler
    const handleSoftReset = () => {
        // Check for quilt generation using multiple indicators
        if (generated || quiltImageUrl || quiltGrid.length > 0) {
            openSoftResetModal();
        } else {
            // If nothing is generated yet, there's nothing to soft reset
            setShowWarning("No layout has been generated yet.");
            
            // Auto-clear the warning after 3 seconds
            setTimeout(() => {
                setShowWarning("");
            }, 3000);
        }
    };

    // Perform the soft reset
    const confirmSoftReset = () => {
        softResetHandler(  // Use the renamed import instead of calling the local function recursively
            uploadedImages,
            gridSize,
            calculateRequiredImages,
            setGenerated,
            setQuiltImageUrl,
            setPreviousLayoutType,
            setGenerateDisabled,
            setIsEditUnlocked,
            setQuiltGrid,
            setFeedbackSubmitted,
            setRating,
            setAIGeneratedLayouts,
            setUploadedImages,
            setGridSize,
            setRequiredImages,
            closeSoftResetModal,
            currentUser,
            db,
            setShowWarning,
            setLayoutSuggestions  // Make sure this parameter is passed correctly
        );

        // Make sure we're going back to step 2 after a soft reset
        setCurrentStep(2);
        
        // Set the navigation flags to prevent auto-navigation
        sessionStorage.setItem("explicitNavigation", "true");
        sessionStorage.setItem("stayOnStep2", "true");
        
        // Also explicitly set the payment-related states to their initial values
        setIsPaymentCompleted(false);
        setIsPremiumUser(false);
        
        // Clear any payment-related session storage as well
        sessionStorage.removeItem("paymentCompleted");
        sessionStorage.removeItem("isPremiumUser");
        sessionStorage.removeItem("fromAIClick");
    };


// Handle dimensions change from QuiltSetup
const handleDimensionsChange = (dimensions) => {
    processDimensionsChange(
        dimensions, 
        generated, 
        uploadedImages, 
        setQuiltDimensions, 
        setRequiredImages, 
        setGridSize, 
        gridSize, 
        setShowWarning
    );
};

    // Add a function to handle the advanced crop editor save
const handleAdvancedCropSave = (rotation, cropInfo, previewUrl) => {
    if (editingImageIndex === null || 
        editingImageIndex < 0 || 
        editingImageIndex >= uploadedImages.length) {
        console.error("Invalid editingImageIndex:", editingImageIndex);
        return;
    }
    
    const currentImage = uploadedImages[editingImageIndex];
    if (!currentImage) {
        console.error("Image not found at index", editingImageIndex);
        return;
    }
    
    console.log(`Saving crop settings for image ${editingImageIndex}:`, { 
        rotation, 
        cropInfo,
        hasPreview: !!previewUrl 
    });
    
    // Create a new array with the updated image
    const updatedImages = [...uploadedImages];
    updatedImages[editingImageIndex] = {
        ...currentImage,
        rotation,
        crop: cropInfo,
        previewUrl,
        adjusted: true,
        processed: false,  // Always mark as unprocessed so it will be processed again
        adjustmentTimestamp: Date.now() // Add timestamp to force reprocessing
    };
    
    // Update the state with the new images array
    setUploadedImages(updatedImages);
    setIsAdvancedEditorOpen(false);
    setEditingImageIndex(null);
    
    // IMPORTANT: Get the correct original URL, not the tile URL
    // Always use originalUrl or originalTshirtUrl if available instead of the tile URL
    const originalImageUrl = currentImage.originalUrl || currentImage.originalTshirtUrl || currentImage.url;
    
    console.log("Using URL for crop settings:", {
        originalUrl: currentImage.originalUrl,
        originalTshirtUrl: currentImage.originalTshirtUrl,
        url: currentImage.url,
        selected: originalImageUrl
    });
    
    // Save crop settings to backend with clear crop parameters that match our UI representation
    saveCropSettings(originalImageUrl, rotation, cropInfo, sessionId, quiltDimensions, setIsProcessingImages, setUploadedImages, setShowWarning);
    
    // Show a message prompting the user to process the image
    setShowWarning("Image adjustments saved. Please process all images before generating the quilt.");
    setTimeout(() => setShowWarning(''), 3000);
};

// Update the function to save crop settings to the server
const saveCropSettings = async (tshirtUrl, rotation, cropInfo) => {
    await processCropSettings(tshirtUrl, rotation, cropInfo, sessionId, quiltDimensions, setIsProcessingImages, setUploadedImages, setShowWarning);
};

// Add or update the function to handle new image upload completion
const handleImageUploaded = (newImage) => {
    processImageUploaded(newImage, setUploadedImages, setShowWarning, setProcessingError, currentUser, db);
};

// Add an enhanced reset detector useEffect
useEffect(() => {
    const handleResetCompletion = () => {
        // This runs after reset operations are completed
        
        // Check if we're in a "reset" state (gridSize is empty and quiltGrid is empty)
        if (gridSize === '' && (!quiltGrid || quiltGrid.length === 0)) {
            console.log("Reset state detected, ensuring QuiltSetup is fully reset");
            
            // Ensure quiltDimensions is fully reset with null values
            setQuiltDimensions({
                quiltWidth: null,
                quiltHeight: null,
                tileWidth: null,
                tileHeight: null,
                columns: 0,
                rows: 0,
                totalTiles: 0,
                tileWidthPixels: 0,
                tileHeightPixels: 0
            });
            
            // Also reset requiredImages to prevent the "Please upload X T-shirts" message
            setRequiredImages(0);
            
            // Reset all form inputs directly
            const resetFormFields = () => {
                const inputFields = document.querySelectorAll('input[type="text"], input[type="number"]');
                inputFields.forEach(input => {
                    // Only reset QuiltSetup fields
                    if (input.name && (input.name.includes('quilt') || input.name.includes('tile'))) {
                        input.value = '';
                        
                        // Trigger React's change handlers
                        const event = new Event('change', { bubbles: true });
                        input.dispatchEvent(event);
                    }
                });
            };
            
            // Execute reset immediately and also after a delay to catch any race conditions
            resetFormFields();
            setTimeout(resetFormFields, 100);
            setTimeout(resetFormFields, 300);
        }
    };
    
    // Call the function when these dependencies change
    handleResetCompletion();
    
}, [gridSize, quiltGrid]);

// Enhance the existing reset useEffect to be more aggressive with form clearing
useEffect(() => {
    // Force reset input fields when user confirms reset
    if (isResetModalOpen === false && isResetLoading === false) {
        // This runs after the reset modal closes and loading is complete
        
        // Create a more thorough reset function for the form fields
        const resetFormFields = () => {
            // Reset all form fields to empty
            const inputFields = document.querySelectorAll('input[type="text"], input[type="number"]');
            inputFields.forEach(input => {
                // Clear any input field value, especially QuiltSetup fields
                if (input.name && (input.name.includes('quilt') || input.name.includes('tile'))) {
                    input.value = '';
                    
                    // For React-controlled inputs, trigger change event to update state
                    const changeEvent = new Event('change', { bubbles: true });
                    input.dispatchEvent(changeEvent);
                }
            });
            
            // Explicitly reset quiltDimensions to null values
            setQuiltDimensions({
                quiltWidth: null,
                quiltHeight: null,
                tileWidth: null,
                tileHeight: null,
                columns: 0,
                rows: 0,
                totalTiles: 0,
                tileWidthPixels: 0,
                tileHeightPixels: 0
            });
        };
        
        // Call immediately and also with a slight delay to ensure DOM is updated
        resetFormFields();
        setTimeout(resetFormFields, 100);
        setTimeout(resetFormFields, 300);
    }
}, [isResetModalOpen, isResetLoading]);

// Update the warning message display logic to ensure it only shows when appropriate
useEffect(() => {
    // Only show warnings if:
    // 1. User has selected a grid size
    // 2. The required images count is valid
    // 3. We have valid quilt dimensions
    // 4. No validation errors in the quilt calculator
    const hasValidGridSize = gridSize && gridSize.includes('x');
    const hasValidDimensions = quiltDimensions.quiltWidth && 
                              quiltDimensions.quiltHeight && 
                              quiltDimensions.totalTiles > 0;
    
    // Don't show any messages if there are validation errors in the quilt size calculator
    if (!isQuiltDimensionsValid()) {
        setShowWarning('');
        return;
    }
    
    // Clear any warnings if basic conditions aren't met
    if (!hasValidGridSize || requiredImages <= 0) {
        setShowWarning('');
        return;
    }
    
    // Calculate required images based on the current grid size - ensures we have the correct count
    const calculatedRequiredImages = calculateRequiredImages(gridSize);
    
    // If the calculated required images doesn't match the state, update it
    if (calculatedRequiredImages !== requiredImages && calculatedRequiredImages > 0) {
        setRequiredImages(calculatedRequiredImages);
        return; // Exit early, the next useEffect run will handle the warning
    }
    
    // This part will only run if all conditions above are met
    if (uploadedImages.length < requiredImages) {
        // If no images uploaded yet, show initial message
        if (uploadedImages.length === 0) {
            setShowWarning(`Upload ${requiredImages} T-shirts for the ${gridSize} grid.`);
        } else {
            // If some images uploaded, show remaining count
            setShowWarning(`Please upload ${requiredImages - uploadedImages.length} more T-shirts for the ${gridSize} grid.`);
        }
    } else if (uploadedImages.length > requiredImages) {
        setShowWarning(`You have uploaded more than required. You can only upload ${requiredImages} T-shirts for the ${gridSize} grid.`);
    } else if (uploadedImages.length === requiredImages) {
        setShowWarning('All T-shirts uploaded! Ready to generate your quilt.'); // Success message when all required images are uploaded
    } else {
        setShowWarning(''); // Clear warning when exact number of images is uploaded
    }
}, [gridSize, requiredImages, uploadedImages.length, quiltDimensions]);

// Add this new function to validate if dimensions are error-free
const isQuiltDimensionsValid = () => {
    // Only log in debug mode when explicitly needed
    const DEBUG = false;
    
    // Skip validation completely if we're in initial state with no grid size
    // This prevents unnecessary console warnings during initial load or resets
    if (!gridSize || gridSize === '') {
        return false;
    }

    // Check if gridSize is valid
    if (!gridSize.includes('x')) {
        DEBUG && console.log("Invalid dimensions: gridSize format is invalid");
        return false;
    }

    // Get the dimension values directly from form fields as a double-check
    // Add null checks and default to empty string to prevent errors
    const quiltWidthInput = document.querySelector('input[name="quiltWidth"]')?.value || '';
    const quiltHeightInput = document.querySelector('input[name="quiltHeight"]')?.value || '';
    const tileWidthInput = document.querySelector('input[name="tileWidth"]')?.value || '';
    const tileHeightInput = document.querySelector('input[name="tileHeight"]')?.value || '';
    
    // Check input fields first - if any are empty or invalid, validation fails
    if (!quiltWidthInput || !quiltHeightInput || !tileWidthInput || !tileHeightInput) {
        DEBUG && console.log("Invalid dimensions: missing form input values", {
            quiltWidthInput, quiltHeightInput, tileWidthInput, tileHeightInput
        });
        return false;
    }
    
    // Parse input values to numbers for additional validation
    const quiltWidthValue = parseFloat(quiltWidthInput);
    const quiltHeightValue = parseFloat(quiltHeightInput);
    const tileWidthValue = parseFloat(tileWidthInput);
    const tileHeightValue = parseFloat(tileHeightInput);
    
    // Check for NaN values
    if (isNaN(quiltWidthValue) || isNaN(quiltHeightValue) || 
        isNaN(tileWidthValue) || isNaN(tileHeightValue)) {
        DEBUG && console.log("Invalid dimensions: non-numeric input values");
        return false;
    }
    
    // Define size limits
    const quiltSizeLimits = { min: 4, max: 8 }; // feet
    const tileSizeLimits = { min: 6, max: 15 }; // inches
    
    // Check dimension values directly from inputs
    if (tileHeightValue < tileSizeLimits.min) {
        DEBUG && console.log("Invalid dimensions: tile height too small from input", tileHeightValue);
        return false;
    }
    
    if (tileWidthValue < tileSizeLimits.min) {
        DEBUG && console.log("Invalid dimensions: tile width too small from input", tileWidthValue);
        return false;
    }
    
    // Now also check state values
    if (!quiltDimensions.quiltWidth || 
        !quiltDimensions.quiltHeight || 
        !quiltDimensions.tileWidth || 
        !quiltDimensions.tileHeight) {
        DEBUG && console.log("Invalid dimensions: missing values in state");
        return false;
    }
    
    // Critical check: ensure tile height meets minimum requirement (common failure point)
    if (quiltDimensions.tileHeight < tileSizeLimits.min) {
        DEBUG && console.log("Invalid dimensions: tile height too small in state", quiltDimensions.tileHeight);
        return false;
    }
    
    if (quiltDimensions.tileWidth < tileSizeLimits.min) {
        DEBUG && console.log("Invalid dimensions: tile width too small in state", quiltDimensions.tileWidth);
        return false;
    }
    
    // Check size limits
    if (quiltDimensions.quiltWidth < quiltSizeLimits.min || 
        quiltDimensions.quiltWidth > quiltSizeLimits.max ||
        quiltDimensions.quiltHeight < quiltSizeLimits.min || 
        quiltDimensions.quiltHeight > quiltSizeLimits.max) {
        DEBUG && console.log("Invalid dimensions: quilt size out of range");
        return false;
    }
    
    if (quiltDimensions.tileWidth > tileSizeLimits.max) {
        DEBUG && console.log("Invalid dimensions: tile width too large", quiltDimensions.tileWidth);
        return false;
    }
    
    if (quiltDimensions.tileHeight > tileSizeLimits.max) {
        DEBUG && console.log("Invalid dimensions: tile height too large", quiltDimensions.tileHeight);
        return false;
    }
    
    // Check for error messages in the DOM
    const errorElements = document.querySelectorAll('.text-red-500');
    const hasValidationErrors = Array.from(errorElements).some(el => 
        el.textContent && 
        el.textContent.includes('Min') || 
        el.textContent.includes('Max') ||
        el.textContent.includes('required')
    );
    
    if (hasValidationErrors) {
        DEBUG && console.log("Invalid dimensions: validation errors found in UI");
        return false;
    }
    
    // Check quilt width-height constraint
    if (quiltDimensions.quiltWidth > quiltDimensions.quiltHeight) {
        DEBUG && console.log("Invalid dimensions: quilt width > height constraint violated");
        return false;
    }
    
    // Check tile width-height constraint
    if (quiltDimensions.tileWidth > quiltDimensions.tileHeight) {
        DEBUG && console.log("Invalid dimensions: tile width > height constraint violated");
        return false;
    }
    
    // Additional validation for reasonable grid size
    const columnsFromDimensions = Math.floor((quiltDimensions.quiltWidth * 12) / quiltDimensions.tileWidth);
    const rowsFromDimensions = Math.floor((quiltDimensions.quiltHeight * 12) / quiltDimensions.tileHeight);
    
    if (columnsFromDimensions < 4 || rowsFromDimensions < 4) {
        DEBUG && console.log("Invalid dimensions: resulting grid too small from calculation", 
            {cols: columnsFromDimensions, rows: rowsFromDimensions});
        return false;
    }
    
    // All checks passed
    DEBUG && console.log("All dimension validation checks passed");
    return true;
};

useEffect(() => {
    const locationState = location.state;
    
    // Check if we're returning from the layout suggestions page
    if (locationState && locationState.aiLayoutsGenerated) {
        console.log("Returned from layout suggestions page. Loading AI layouts.");
        
        // Fetch the layouts from the session data
        const loadAILayouts = async () => {
            if (currentUser && currentUser.uid) {
                try {
                    const sessionRef = doc(db, 'sessions', currentUser.uid);
                    const sessionSnap = await getDoc(sessionRef);
                    
                    if (sessionSnap.exists()) {
                        const sessionData = sessionSnap.data();
                        
                        console.log("Session data loaded:", sessionData);
                        
                        // Use the grid size from session data if available, otherwise use current state
                        const effectiveGridSize = sessionData.gridSize || gridSize;
                        
                        // First check for aiGeneratedLayouts
                        if (Array.isArray(sessionData.aiGeneratedLayouts) && 
                            sessionData.aiGeneratedLayouts.length > 0) {
                            
                            console.log("Found aiGeneratedLayouts in session data:", sessionData.aiGeneratedLayouts);
                            
                            // Convert matrices from Firestore format if needed
                            const processedLayouts = sessionData.aiGeneratedLayouts.map(layout => {
                                if (layout.matrix) {
                                    // Use layout-specific grid size if available, otherwise use effective grid size
                                    const layoutGridSize = layout.original_grid_size || effectiveGridSize;
                                    console.log(`Converting matrix for "${layout.layout_name}" using grid size: ${layoutGridSize}`);
                                    
                                    try {
                                        const matrix = convertFirestoreToMatrix(layout.matrix, layoutGridSize);
                                        return { ...layout, matrix };
                                    } catch (error) {
                                        console.error(`Error converting matrix for "${layout.layout_name}":`, error);
                                        // Create a fallback matrix
                                        const [cols, rows] = (layoutGridSize).split('x').map(Number);
                                        const fallbackMatrix = Array(rows).fill().map((_, r) => 
                                            Array(cols).fill().map((_, c) => `${r},${c}`)
                                        );
                                        return { ...layout, matrix: fallbackMatrix };
                                    }
                                }
                                return layout;
                            });
                            
                            setAIGeneratedLayouts(processedLayouts);
                        } 
                        // Fall back to old 'layouts' field if aiGeneratedLayouts not found
                        else if (Array.isArray(sessionData.layouts) && 
                                 sessionData.layouts.length > 0) {
                            
                            console.log("Found layouts array in session data:", sessionData.layouts);
                            
                            // Process the same way as aiGeneratedLayouts
                            const processedLayouts = sessionData.layouts.map(layout => {
                                if (layout.matrix) {
                                    const matrix = convertFirestoreToMatrix(layout.matrix, gridSize);
                                    return { ...layout, matrix };
                                }
                                return layout;
                            });
                            
                            setAIGeneratedLayouts(processedLayouts);
                        } else {
                            console.warn("No AI layouts found in session data");
                        }
                    } else {
                        console.error("Session document does not exist");
                    }
                        
                    // Wait for UI to update before scrolling
                    setTimeout(() => {
                        let aiLayoutsSection = document.getElementById("ai-layouts-section");
                        if (aiLayoutsSection) {
                            aiLayoutsSection.scrollIntoView({ behavior: 'smooth' });
                            console.log("Scrolled to AI layouts section");
                        } else {
                            console.warn("AI layouts section element not found");
                        }
                    }, 1000);
                } catch (error) {
                    console.error("Error loading AI layouts:", error);
                }
            } else {
                console.warn("No current user available to load layouts");
            }
        };
        
        loadAILayouts();
    }
}, [location.state, currentUser, gridSize, db]);

    // Add step management state

    
    // Add state for layout suggestions editing
    const [layoutSuggestions, setLayoutSuggestions] = useState([]);
    
    // Add state for selected layout for Step6
    const [selectedLayout, setSelectedLayout] = useState(null);

    // Determine the initial step based on the application state
    const determineInitialStep = () => {
        // Check for valid quilt dimensions
        const hasValidDimensions = quiltDimensions.quiltWidth && 
                               quiltDimensions.quiltHeight && 
                               quiltDimensions.tileWidth && 
                               quiltDimensions.tileHeight;
    
        // If quilt is generated and we have AI layouts, show step 5
        if (quiltImageUrl && aiGeneratedLayouts && aiGeneratedLayouts.length > 0) {
            return 5;
        }
        // If quilt is generated and we have layout suggestions, show step 4
        else if (quiltImageUrl && layoutSuggestions && layoutSuggestions.length > 0) {
            return 4;
        }
        // If quilt is generated but no AI layouts or suggestions, show step 3
        else if (quiltImageUrl) {
            return 3;
        }
        // If images are uploaded but no quilt yet, show step 2
        else if (uploadedImages && uploadedImages.length > 0) {
            return 2;
        }
        // If dimensions are set but no images, show step 2
        else if (hasValidDimensions && gridSize && gridSize.includes('x')) {
            return 2;
        }
        // Otherwise, start at step 1
        return 1;
    };
    
    // Update current step when component state changes
    useEffect(() => {
        // Only use determineInitialStep for initial loading or reset scenarios
        // Don't override manual navigation by the user
        const shouldDetermineStep = 
            // Only recalculate step if we haven't navigated manually yet
            currentStep === 1 && 
            // And we have some state that would trigger a different step
            (uploadedImages.length > 0 || quiltImageUrl || 
             (aiGeneratedLayouts && aiGeneratedLayouts.length > 0)) &&
            // ADD THIS FLAG to prevent auto-navigation after manual navigation
            !sessionStorage.getItem("explicitNavigation");
        
        if (shouldDetermineStep) {
            const appropriateStep = determineInitialStep();
            setCurrentStep(appropriateStep);
        }
    }, [quiltImageUrl, aiGeneratedLayouts, uploadedImages.length, currentStep]);
    
    // Move to a specific step with validation
const navigateToStep = (step) => {
    console.log(`Navigating from step ${currentStep} to step ${step}`);
    
    // Set explicit navigation flag
    sessionStorage.setItem("explicitNavigation", "true");
    
    // Special case for navigating to step 4 - make sure we first load layout suggestions
    if (step === 4) {
        // First check if all required images are uploaded and a basic layout is generated
        if (uploadedImages.length < requiredImages || !quiltImageUrl) {
            console.log("Cannot navigate to step 4: images not uploaded or basic layout not generated");
            setShowWarning("Please upload all required images and generate a basic layout first.");
            
            // Force user to the appropriate step
            if (uploadedImages.length < requiredImages) {
                setCurrentStep(2); // Go to image upload step
            } else {
                setCurrentStep(3); // Go to basic layout step
            }
            return;
        }
        
        // Attempt to load layout suggestions from Firestore if they're not already loaded
        if (!layoutSuggestions || layoutSuggestions.length === 0) {
            console.log("Loading layout suggestions before navigating to step 4");
            
            // Load layout suggestions from Firestore only - no fallbacks
            const loadLayoutSuggestions = async () => {
                try {
                    if (currentUser && currentUser.uid) {
                        const sessionRef = doc(db, 'sessions', currentUser.uid);
                        const sessionSnap = await getDoc(sessionRef);
                        
                        if (sessionSnap.exists()) {
                            const sessionData = sessionSnap.data();
                            
                            // Use layoutSuggestions from Firestore if available
                            if (sessionData.layoutSuggestions && 
                                Array.isArray(sessionData.layoutSuggestions) && 
                                sessionData.layoutSuggestions.length > 0) {
                                
                                console.log("Found layout suggestions in Firestore:", sessionData.layoutSuggestions);
                                setLayoutSuggestions(sessionData.layoutSuggestions);
                                
                                // Store in sessionStorage for backup
                                try {
                                    sessionStorage.setItem('firestoreSessionData', JSON.stringify({
                                        layoutSuggestions: sessionData.layoutSuggestions
                                    }));
                                } catch (err) {
                                    console.error("Error storing layoutSuggestions in sessionStorage:", err);
                                }
                                
                                // Now proceed with navigation after ensuring suggestions are loaded
                                setCurrentStep(step);
                                return;
                            } else {
                                console.log("No layout suggestions found in Firestore.");
                                // If no layout suggestions found, go back to step 3
                                setShowWarning("No layout suggestions found. Please generate them from the Basic Layout page first.");
                                setCurrentStep(3);
                                return;
                            }
                        } else {
                            console.error("Session document does not exist");
                        }
                    }
                } catch (error) {
                    console.error("Error loading layout suggestions:", error);
                }
                
                // If we reach here, something went wrong
                setShowWarning("Could not load layout suggestions. Please go back and generate them first.");
                setCurrentStep(3);
                return; // Exit the function to prevent immediate navigation
            };
            
            // Execute the function to load suggestions
            loadLayoutSuggestions();
            return; // Exit the function to prevent immediate navigation
        }
    }
    
    // Special case for navigating directly to step 5 (AI Layouts)
    if (step === 5) {
        // Check if all required images are uploaded and a basic layout is generated
        if (uploadedImages.length < requiredImages || !quiltImageUrl) {
            console.log("Cannot navigate to step 5: images not uploaded or basic layout not generated");
            setShowWarning("Please upload all required images and generate a basic layout first.");
            
            // Force user to the appropriate step
            if (uploadedImages.length < requiredImages) {
                setCurrentStep(2); // Go to image upload step
            } else {
                setCurrentStep(3); // Go to basic layout step
            }
            return;
        }
        
        // Check if AI layouts have been generated
        if (!aiGeneratedLayouts || aiGeneratedLayouts.length === 0) {
            setShowWarning("Please generate AI layouts from the Basic Layout page first.");
            setCurrentStep(3);
            return;
        }
    }
    
    // If AI layouts exist and user tries to go to step 4, redirect to step 5 (AI Layouts)
    if (step === 4 && aiGeneratedLayouts && aiGeneratedLayouts.length > 0) {
        step = 5; // Skip to step 5 (which appears as step 4 in UI)

   

    }
    
    // Add validation to ensure step is within valid range
    if (step < 1) step = 1;
    if (step > totalSteps) step = totalSteps;
    
    // Validation to prevent advancing without completing current step
    if (step > currentStep) {
        // Always check dimensions before advancing to any step beyond step 1
        const hasValidDimensions = quiltDimensions.quiltWidth && 
                                  quiltDimensions.quiltHeight && 
                                  quiltDimensions.tileWidth && 
                                  quiltDimensions.tileHeight &&
                                  gridSize && gridSize.includes('x');
        
        // If dimensions aren't valid, don't allow advancing past step 1
        if (!hasValidDimensions) {
            setShowWarning("Please set valid quilt dimensions first");
            setCurrentStep(1); // Force to step 1
            return;
        }
        
        // Check if required images are uploaded before going to step 3 or beyond
        if (step >= 3) {
            if (uploadedImages.length < requiredImages) {
                setShowWarning(`Please upload all ${requiredImages} required images first`);
                setCurrentStep(2); // Force to step 2
                return;
            }
            
            // Check if basic layout is generated before going to step 4 or beyond
            if (step >= 4 && !quiltImageUrl) {
                setShowWarning("Please generate a basic layout first");
                setCurrentStep(3); // Force to step 3
                return;
            }
        }
    } 
    // Special handling for moving back to Step 2 from Step 3 or beyond
    else if (currentStep >= 3 && step === 2) {
        console.log("Moving back from step 3+ to step 2, enabling regeneration");
        // Re-enable the generate button when returning to Step 2
        setGenerateDisabled(false);
        
        // Add a flag to session storage to prevent auto-stepping back to Step 3
        sessionStorage.setItem("stayOnStep2", "true");
    }
    
    // Add a flag to session storage to indicate manual navigation happened
    sessionStorage.setItem("manualNavigation", "true");
    
    setCurrentStep(step);
    setShowWarning(''); // Clear warnings when changing steps
    
    // Scroll to top when changing steps
    window.scrollTo({ top: 0, behavior: 'smooth' });
};
    
    // Next step button handler
const nextStep = () => {
    sessionStorage.setItem("explicitNavigation", "true");
    
    // If moving from step 1 to step 2, explicitly save dimensions first
    if (currentStep === 1) {
        // Calculate pixel dimensions based on inches
        const updatedDimensions = {
            ...quiltDimensions,
            tileWidthPixels: Math.round(300 * (quiltDimensions.tileWidth / quiltDimensions.tileHeight)),
            tileHeightPixels: 300
        };
        
        // Update state with calculated pixel dimensions
        setQuiltDimensions(updatedDimensions);
        
        // Explicitly save the dimensions to Firestore
        if (currentUser) {
            const sessionRef = doc(db, 'sessions', currentUser.uid);
            setDoc(sessionRef, { 
                quiltDimensions: updatedDimensions,
                gridSize: gridSize,
                lastUpdated: new Date()
            }, { merge: true })
            .then(() => {
                console.log("Dimensions saved before moving to step 2");
                navigateToStep(currentStep + 1);
            }).catch(error => {
                console.error("Error saving dimensions:", error);
                // Continue to next step even if save fails
                navigateToStep(currentStep + 1);
            });
            return; // Exit early since we're handling navigation in the promise
        }
    }
    
    navigateToStep(currentStep + 1);
};
    
    // Previous step button handler - Move inside the component
    const prevStep = () => {
        console.log(`prevStep called from step ${currentStep}`);
        
        // Set a flag in session storage to prevent auto-navigation
        sessionStorage.setItem("explicitNavigation", "true");
        
        // Check if we're trying to navigate from step 5 to step 3
        if (currentStep === 5 && sessionStorage.getItem("navigateToStep3") === "true") {
            console.log("Direct navigation to step 3 from step 5 detected");
            navigateToStep(3);
            return;
        }
        
        // Fix for the specific issue where clicking back in step 2 goes to step 5
        if (currentStep === 2) {
            // Explicitly navigate to step 1 when on step 2
            console.log("Navigating from step 2 to step 1");
            navigateToStep(1);
        } else {
            // For other steps, go to the previous step as normal
            navigateToStep(currentStep - 1);
        }
    };
    
    // Auto-advance to step 3 when generation completes
    useEffect(() => {
        // Only auto-advance if:
        // 1. We have a quilt image
        // 2. We're on step 2
        // 3. Manual navigation hasn't happened
        // 4. We don't have the stayOnStep2 flag set
        // 5. We don't have the explicitNavigation flag set (ADD THIS CONDITION)
        if (quiltImageUrl && currentStep === 2 && 
            !sessionStorage.getItem("manualNavigation") &&
            !sessionStorage.getItem("stayOnStep2") &&
            !sessionStorage.getItem("explicitNavigation")) {
            console.log("Auto-advancing to step 3 from step 2");
            setCurrentStep(3);
        }
    }, [quiltImageUrl, currentStep]);
    
    // Cleanup navigation flags when component unmounts
    useEffect(() => {
        return () => {
            sessionStorage.removeItem("explicitNavigation");
            sessionStorage.removeItem("manualNavigation");
            sessionStorage.removeItem("stayOnStep2");
        };
    }, []);
    
    // Get step name with updated numbering for display purposes
    const getStepName = (step) => {
        switch(step) {
            case 1: return "Set Dimensions";
            case 2: return "Upload Clothing";
            case 3: return "Basic Layout";
            case 4: return "Edit Suggestions"; // Will be hidden in UI once layouts are generated
            case 5: return "AI Layouts";
            case 6: return "Enhance Layout"; // New step
            default: return "";
        }
    };
    
    // Get display step number (converts internal step 5 to display step 4 when AI layouts exist)
    const getDisplayStepNumber = (step) => {
        // If AI layouts exist, hide step 4 (Edit Suggestions)
        if (aiGeneratedLayouts && aiGeneratedLayouts.length > 0) {
            if (step === 5) return 4;
            if (step === 6) return 5;
            return step > 5 ? step - 1 : step;
        }
        return step;
    };
    
    // Render the step indicator progress bar with updated numbering
    const renderStepIndicator = () => {
        // Calculate which steps to show in the UI
        // If AI layouts exist, hide step 4 (Edit Suggestions)
        const visibleSteps = aiGeneratedLayouts && aiGeneratedLayouts.length > 0 
            ? [1, 2, 3, 5, 6] // Skip step 4 if layouts exist
            : [1, 2, 3, 4, 5, 6]; // Show all steps otherwise
        
        // Calculate display step number
        const displayStep = getDisplayStepNumber(currentStep);
        
        // Calculate the total steps to display in UI
        const displayedTotalSteps = aiGeneratedLayouts && aiGeneratedLayouts.length > 0 ? 5 : 6;
        
        return (
            <div className="mb-4 sm:mb-8">
                {/* Progress bar - updated to sage green */}
                <div className="relative pt-1 mb-3 sm:mb-4">
                    <div className="flex mb-2 items-center justify-between">
                        <div>
                            <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-[#9CAF88] bg-[#9CAF88] bg-opacity-20">
                                Step {displayStep} of {displayedTotalSteps}
                            </span>
                        </div>
                        <div className="text-right">
                            <span className="text-xs font-semibold inline-block text-[#9CAF88]">
                                {Math.round((displayStep / displayedTotalSteps) * 100)}%
                            </span>
                        </div>
                    </div>
                    <div className="overflow-hidden h-2 mb-2 sm:mb-4 text-xs flex rounded bg-[#9CAF88] bg-opacity-20">
                        <div 
                            style={{ width: `${(displayStep / displayedTotalSteps) * 100}%` }}
                            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[#9CAF88] transition-all duration-500"
                        ></div>
                    </div>
                </div>
                
                {/* Desktop step labels (only show on larger screens) - updated to sage green */}
                <div className="hidden sm:flex justify-between">
                    {visibleSteps.map((step, index) => (
                        <div 
                            key={step}
                            className={`relative flex flex-col items-center ${step <= currentStep ? 'text-[#9CAF88]' : 'text-gray-400'}`}
                        >
                            <div 
                                className={`rounded-full transition duration-500 ease-in-out h-10 w-10 sm:h-12 sm:w-12 flex items-center justify-center ${
                                    step < currentStep ? 'bg-[#9CAF88] text-white' : step === currentStep ? 'border-2 border-[#9CAF88]' : 'border-2 border-gray-300'
                                }`}
                                onClick={() => navigateToStep(step)}
                                role="button"
                                aria-label={`Go to step ${index + 1}`}
                            >
                                {step < currentStep ? (
                                    <span className="text-white font-bold">✓</span>
                                ) : (
                                    <span className="font-bold">{index + 1}</span>
                                )}
                            </div>
                            <div className="text-center mt-2">
                                <span className={`text-xs sm:text-sm font-medium ${step <= currentStep ? 'text-[#9CAF88]' : 'text-gray-500'}`}>
                                    {/* Show "AI Layouts" for step 5, but as step 4 in the UI when layouts exist */}
                                    {step === 5 && aiGeneratedLayouts && aiGeneratedLayouts.length > 0 
                                        ? "AI Layouts" 
                                        : getStepName(step)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Mobile step indicator - simpler dots with current step label - updated to sage green */}
                <div className="flex flex-col items-center sm:hidden">
                    <div className="flex justify-center mb-2">
                        {visibleSteps.map((step, index) => (
                            <div 
                                key={step}
                                className={`h-2 mx-1 rounded-full transition-all ${
                                    step === currentStep 
                                        ? 'w-8 bg-[#9CAF88]' 
                                        : step < currentStep 
                                            ? 'w-2 bg-[#9CAF88] bg-opacity-70' 
                                            : 'w-2 bg-gray-300'
                                }`}
                                onClick={() => navigateToStep(step)}
                            />
                        ))}
                    </div>
                    <div className="text-sm font-medium text-[#9CAF88] mt-1">
                        {currentStep === 5 && aiGeneratedLayouts && aiGeneratedLayouts.length > 0 
                            ? "AI Layouts" 
                            : getStepName(currentStep)}
                    </div>
                </div>
            </div>
        );
    };

    // Step 1: Quilt Dimensions
const renderQuiltDimensions = () => {
    return (
        <Step1 
            quiltDimensions={quiltDimensions}
            handleDimensionsChange={handleDimensionsChange}
            isQuiltDimensionsValid={isQuiltDimensionsValid}
            gridSize={gridSize}
            requiredImages={requiredImages}
            generated={generated}
            uploadedImages={uploadedImages}
            showWarning={showWarning}
            nextStep={nextStep}
        />
    );
};

// Replace the renderUploadClothing function with this:
const renderUploadClothing = () => {
    return (
        <Step2
            uploadedImages={uploadedImages}
            gridSize={gridSize}
            requiredImages={requiredImages}
            handleImageUploaded={handleImageUploaded}
            handleAdjustImage={handleAdjustImage}
            handleDeleteImage={handleDeleteImage}
            handleGenerateClick={handleGenerateClick}
            loading={loading}
            generateDisabled={generateDisabled}
            sessionId={sessionId}
            isProcessingImages={isProcessingImages}
            processingError={processingError}
            showWarning={showWarning}
            prevStep={prevStep}
            nextStep={nextStep}
            quiltImageUrl={quiltImageUrl}
            currentStep={currentStep}
        />
    );
};

// Replace the renderBasicLayout function with this:
const renderBasicLayout = () => {
    return (
        <Step3
            gridSize={gridSize}
            quiltGrid={quiltGrid}
            handleEditClick={handleEditClick}
            handleDownloadClick={handleDownloadClick}
            handleShareClick={handleShareClick}
            handleAIClick={handleAIClick}
            loadingAI={loadingAI}
            aiGeneratedLayouts={aiGeneratedLayouts}
            isPremiumUser={isPremiumUser}
            userCountry={userCountry}
            calculatePrice={calculatePrice}
            currentUser={currentUser}
            storage={storage}
            db={db} // Pass db parameter
            nextStep={nextStep}
            prevStep={prevStep}
            logoSrc={logoSrc}
            layoutSuggestions={layoutSuggestions} // Pass layoutSuggestions
            layout_suggestions={layoutSuggestions} // Pass as layout_suggestions also for compatibility
        />
    );
};

    // Step 4: Edit Layout Suggestions
    const renderEditSuggestions = () => {
        console.log("Rendering Step4 with layoutSuggestions:", layoutSuggestions);
        return (
            <Step4
                layoutSuggestions={layoutSuggestions}
                handleTitleChange={handleTitleChange}
                handleDescriptionChange={handleDescriptionChange}
                handleGenerateLayouts={handleGenerateLayouts}
                loadingAI={loadingAI}
                aiError={aiError}
                prevStep={prevStep}
                nextStep={nextStep}
                aiGeneratedLayouts={aiGeneratedLayouts}
                quiltGrid={quiltGrid}
                gridSize={gridSize}
                logoSrc={logoSrc}
            />
        );
    };
    
    // Step 5: AI Layouts
    const renderAILayouts = () => {
        return (
            <Step5
                aiGeneratedLayouts={aiGeneratedLayouts}
                quiltGrid={quiltGrid}
                gridSize={gridSize}
                logoSrc={logoSrc}
                currentUser={currentUser}
                storage={storage}
                db={db}
                handleDownloadClick={handleDownloadClick}
                handleShareClick={handleShareClick}
                prevStep={prevStep}
                setShowModal={setShowModal}
                setShareLink={setShareLink}
                onSelectLayout={handleSelectLayout}  // Add this prop
            />
        );
    };

    // Step 6: AI Enhanced Layout Customization
    const renderEnhanceLayout = () => {
        if (!selectedLayout) {
            // If no layout is selected, go back to AI layouts
            setCurrentStep(5);
            return null;
        }
        
        return (
            <Step6
                selectedLayout={selectedLayout}
                prevStep={() => navigateToStep(5)}
                currentUser={currentUser}
                storage={storage}
                db={db}
                handleDownloadClick={handleDownloadClick}
                handleShareClick={handleShareClick}
                setShowModal={setShowModal}
                setShareLink={setShareLink}
            />
        );
    };

    // Add handler for title change in layout suggestions
const handleTitleChange = (index, newTitle) => {
    const updatedSuggestions = [...layoutSuggestions];
    if (updatedSuggestions[index]) {
        updatedSuggestions[index] = {
            ...updatedSuggestions[index],
            title: newTitle
        };
        setLayoutSuggestions(updatedSuggestions);
    }
};

// Add handler for description change in layout suggestions
const handleDescriptionChange = (index, newDescription) => {
    const updatedSuggestions = [...layoutSuggestions];
    if (updatedSuggestions[index]) {
        updatedSuggestions[index] = {
            ...updatedSuggestions[index],
            description: newDescription
        };
        setLayoutSuggestions(updatedSuggestions);
    }
};

    // Add handler for generating layouts from suggestions
    const handleGenerateLayouts = async () => {
        try {
            setLoadingAI(true);
            setAIError(null);

            // Format layout suggestions
            const formattedLayouts = layoutSuggestions.map(layout => ({
                title: layout.title,
                description: layout.description
            }));

            console.log("Sending layout suggestions to API:", formattedLayouts);

            // Call the generate-layouts endpoint
            const response = await fetch(API_ENDPOINTS.GENERATE_LAYOUTS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    layout_suggestions: formattedLayouts
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                // Show user-friendly error if it's an AI/Anthropic error
                if (
                    errorText.includes('Anthropic') ||
                    errorText.includes('Claude') ||
                    errorText.includes('overloaded') ||
                    errorText.includes('429') ||
                    errorText.includes('timeout') ||
                    errorText.includes('rate limit') ||
                    errorText.includes('temporarily unavailable')
                ) {
                    throw new Error("Our AI service is currently busy or unavailable. Please try again after some time.");
                }
                throw new Error(`Error generating layouts: ${errorText}`);
            }

            // Process the response
            const responseData = await response.json();
            console.log("API response:", responseData);
            
            // Extract the layouts data with improved error handling
            let layoutsData = [];
            
            if (responseData.suggestions) {
                try {
                    // Try to parse if it's a string
                    if (typeof responseData.suggestions === 'string') {
                        // First try to extract JSON from markdown code blocks
                        const jsonMatch = responseData.suggestions.match(/```(?:json)?\s*([\s\S]*?)```/);
                        if (jsonMatch && jsonMatch[1]) {
                            layoutsData = JSON.parse(jsonMatch[1]);
                        } else {
                            // If no code blocks, try parsing the entire string
                            layoutsData = JSON.parse(responseData.suggestions);
                        }
                    } else {
                        // If it's already an object, use it directly
                        layoutsData = responseData.suggestions;
                    }
                    
                    // Check if we have a "layouts" property in the parsed data
                    if (layoutsData.layouts && Array.isArray(layoutsData.layouts)) {
                        layoutsData = layoutsData.layouts;
                    }
                    
                    // Ensure we have an array
                    if (!Array.isArray(layoutsData)) {
                        console.warn("Layouts data is not an array, creating default layouts");
                        layoutsData = createDefaultLayouts(formattedLayouts);
                    }
                } catch (error) {
                    console.error("Error parsing JSON from response:", error);
                    console.error("Raw suggestions:", responseData.suggestions);
                    
                    // Create default layouts based on the suggestions
                    layoutsData = createDefaultLayouts(formattedLayouts);
                }
            } else {
                console.warn("No suggestions found in response, creating default layouts");
                layoutsData = createDefaultLayouts(formattedLayouts);
            }

            console.log("Processed layouts data:", layoutsData);
            
            // Update the state with the new layouts
            setAIGeneratedLayouts(layoutsData);
            
            if (currentUser && currentUser.uid) {
                try {
                    await saveAILayouts(currentUser, db, layoutsData, gridSize);
                } catch (error) {
                    console.error("Error saving layouts to Firestore:", error);
                }
            }
            
            // Move to the AI layouts step
            setCurrentStep(5);
        } catch (error) {
            console.error("Error generating layouts:", error);
            // Show user-friendly error if it's an AI/Anthropic error
            if (
                error.message &&
                (
                    error.message.includes('Anthropic') ||
                    error.message.includes('Claude') ||
                    error.message.includes('overloaded') ||
                    error.message.includes('429') ||
                    error.message.includes('timeout') ||
                    error.message.includes('rate limit') ||
                    error.message.includes('temporarily unavailable')
                )
            ) {
                setAIError("Our AI service is currently busy or unavailable. Please try again after some time.");
            } else {
                setAIError(error.message);
            }
        } finally {
            setLoadingAI(false);
        }
    };

// Helper function to create default layouts based on suggestions
const createDefaultLayouts = (suggestions) => {
    return suggestions.map((suggestion, index) => ({
        title: suggestion.title || `Layout ${index + 1}`,
        description: suggestion.description || "Custom layout generated based on your suggestion",
        matrix: [] // Initialize with empty matrix, will be filled in enhancement step
    }));
};

// Handle selecting a layout to customize in Step6
const handleSelectLayout = (layout) => {
    console.log("Original layout selected:", layout);
    
    // Create a deep copy of the layout to avoid modifying the original
    const selectedLayoutWithTiles = {
        ...layout,
        tileData: [] // Will hold individual tile information
    };
    
    // Extract the matrix data to get tile positions
    let matrix = layout.matrix;
    
    // Handle different matrix formats (object from Firestore or array)
    if (!matrix) {
        console.error("No matrix data in selected layout");
        return;
    }
    
    // FIXED: Better matrix conversion handling
    if (typeof matrix === 'object' && !Array.isArray(matrix)) {
        console.log("Converting object matrix to 2D array");
        const layoutGridSize = layout.original_grid_size || gridSize;
        const [cols, rows] = layoutGridSize.split('x').map(Number);
        
        // Create empty 2D array
        const matrixArray = Array(rows).fill().map(() => Array(cols).fill(""));
        
        // Fill the array from the object representation
        Object.entries(matrix).forEach(([key, value]) => {
            const [rowIndex, colIndex] = key.split('_').map(Number);
            if (rowIndex >= 0 && rowIndex < rows && colIndex >= 0 && colIndex < cols) {
                matrixArray[rowIndex][colIndex] = value;
            }
        });
        
        matrix = matrixArray;
        console.log("Converted matrix:", matrix);
        
        // Update the layout object with the converted matrix
        selectedLayoutWithTiles.matrix = matrixArray;
    }
    
    // Validate matrix is now an array
    if (!Array.isArray(matrix) || matrix.length === 0) {
        console.error("No valid matrix data in selected layout after conversion");
        return;
    }

    // Create a reference to the tile data from quiltGrid
    const tileMap = {};
    if (quiltGrid && quiltGrid.length > 0) {
        quiltGrid.forEach(tile => {
            if (tile && tile.position) {
                const posKey = `${tile.position[0]},${tile.position[1]}`;
                tileMap[posKey] = tile;
            }
        });
    }
    
    // Extract rows and columns from matrix
    const rows = matrix.length;
    const cols = matrix[0] ? matrix[0].length : 0;
    
    if (rows === 0 || cols === 0) {
        console.error("Invalid matrix dimensions");
        return;
    }
    
    // For each position in the matrix, find the corresponding tile
    const tileData = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            // Get the matrix value at this position
            const matrixValue = matrix[r] && matrix[r][c] ? matrix[r][c] : null;
            
            if (!matrixValue) {
                console.warn(`No matrix value at position [${r},${c}]`);
                continue;
            }
            
            // Look up the original tile based on matrix value or position
            let originalTile;
            if (typeof matrixValue === 'string' && matrixValue.includes(',')) {
                // Matrix value is a position string like "0,1"
                originalTile = tileMap[matrixValue];
            } else if (Array.isArray(matrixValue) && matrixValue.length === 2) {
                // Matrix value is an array like [0, 1]
                originalTile = tileMap[`${matrixValue[0]},${matrixValue[1]}`];
            } else {
                // Try to find by row/col index as fallback
                originalTile = tileMap[`${r},${c}`];
            }
            
            // Create a tile entry with position information
            tileData.push({
                row: r,
                col: c,
                url: originalTile?.url || null,
                originalUrl: originalTile?.originalUrl || originalTile?.url || null,
                matrixValue: matrixValue,
                originalTile: originalTile // Include the full original tile data
            });
        }
    }
    
    // Add the tile data to the layout
    selectedLayoutWithTiles.tileData = tileData;
    selectedLayoutWithTiles.gridSize = `${cols}x${rows}`;
    selectedLayoutWithTiles.isForEnhancement = true; // Flag to indicate this is for enhancement
    
    console.log("Selected layout with tile data:", selectedLayoutWithTiles);
    console.log(`Generated ${tileData.length} tiles for ${rows}x${cols} grid`);
    
    // Store the layout in state and move to Step6
    setSelectedLayout(selectedLayoutWithTiles);
    setCurrentStep(6);
};

    return (
        <QuiltBuilderWithLayout handleReset={openResetModal} handleSoftReset={handleSoftReset} isGenerated={quiltImageUrl}>
            <HotjarIntegration />
            
            <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
                {/* Change the background gradient to sage green */}
                <div className="bg-[#9CAF88] text-white p-4 sm:p-6 rounded-t-lg">
                    <div className="flex justify-between items-center mb-2">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: "Playfair Display" }}>
                            Memory Quilt Designer
                        </h1>
                    </div>
                    <p className="text-white text-opacity-90 text-sm sm:text-base">
                        Design a beautiful memory quilt from your favorite clothing items in {totalSteps} simple steps
                    </p>
                </div>
                
                <div className="h-4"></div>

                <div className="bg-white rounded-b-lg border border-gray-300 p-3 sm:p-6">
                    {renderStepIndicator()}
                    
                    {/* Render the appropriate step content */}
                    {currentStep === 1 && renderQuiltDimensions()}
                    {currentStep === 2 && renderUploadClothing()}
                    {currentStep === 3 && renderBasicLayout()}
                    {currentStep === 4 && renderEditSuggestions()}
                    {currentStep === 5 && renderAILayouts()}
                    {currentStep === 6 && renderEnhanceLayout()}
                    
                    {/* Add QuiltEditor component when editing is enabled */}
                    {isEditing && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-2 sm:p-4 overflow-auto">
                            <QuiltEditor
                                quiltGrid={quiltGrid}
                                gridSize={gridSize}
                                onSave={handleSaveChanges}
                                onCancel={handleCancelEdit}
                            />
                        </div>
                    )}
                </div>
                
            </div>
            
  
            {/* Keep all modals and additional components */}
            <TawkChat />
            
            {/* Image Adjustment Modal */}
            {isAdjustmentModalOpen && currentImageIndex !== null && (
                <ImageAdjustmentModal
                    imageUrl={uploadedImages[currentImageIndex].url}
                    rotation={currentRotation}
                    crop={currentCrop}
                    onSave={handleSaveImageAdjustment}
                    onCancel={handleCancelAdjustment}
                />
            )}
            
            {/* Advanced Crop Editor */}
            {isAdvancedEditorOpen && editingImageIndex !== null && 
             editingImageIndex >= 0 && editingImageIndex < uploadedImages.length && (
                <AdvancedCropEditor
                    key={`crop-editor-${editingImageIndex}-${uploadedImages[editingImageIndex].adjustmentTimestamp || Date.now()}`}
                    imageUrl={uploadedImages[editingImageIndex]?.dataUrl || 
                            uploadedImages[editingImageIndex]?.originalTshirtUrl || 
                            uploadedImages[editingImageIndex]?.originalUrl || 
                            uploadedImages[editingImageIndex]?.url}
                    rotation={uploadedImages[editingImageIndex]?.rotation || 0}
                    cropInfo={uploadedImages[editingImageIndex]?.crop || null}
                    tileWidth={quiltDimensions.tileWidthPixels}
                    tileHeight={quiltDimensions.tileHeightPixels}
                    onSave={handleAdvancedCropSave}
                    onCancel={handleAdvancedEditorCancel}
                />
            )}

            {/* Reset Confirmation Modal */}
            {isResetModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-auto">
                        <div className="p-4 sm:p-6">
                            <h2 className="text-lg sm:text-xl font-semibold" style={{ fontFamily: "Playfair Display" }}>Start New Project?</h2>
                            <p className="mt-2 text-gray-600 text-sm sm:text-base" style={{ fontFamily: "Playfair Display" }}>
                                Your current design will be lost. Please make sure to download your design before starting a new project.
                            </p>
                            <div className="mt-4 flex flex-col sm:flex-row sm:justify-end gap-3 sm:gap-4">
                                <button
                                    className="w-full sm:w-auto px-4 py-2 bg-gray-200 rounded-md text-gray-700 hover:bg-gray-300 order-2 sm:order-1"
                                    onClick={closeResetModal} style={{ fontFamily: "Playfair Display" }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 order-1 sm:order-2"
                                    onClick={() =>
                                        confirmReset(
                                            sessionId,
                                            storage,
                                            db,
                                            setUploadedImages,
                                            setGridSize,
                                            setGenerated,
                                            setRequiredImages,
                                            setShowWarning,
setQuiltImageUrl,
                                            setPreviousLayoutType,
                                            setGenerateDisabled,
                                            setIsEditUnlocked,
                                            setQuiltGrid,
                                            setSessionId,
                                            setFeedbackSubmitted,
                                            setRating,
                                            setIsResetLoading,
                                            setAIGeneratedLayouts,
                                            closeResetModal,
                                            setQuiltDimensions,
                                            setLayoutSuggestions,
                                            setCurrentStep  // Add this parameter
                                        )
                                    }
                                    disabled={isResetLoading}
                                >
                                    {isResetLoading ? (
                                        <>
                                            <span className="spinner"></span>
                                            Loading...
                                        </>
                                    ) : (
                                        "Continue"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Soft Reset Confirmation Modal */}
            {isSoftResetModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-auto">
                        <div className="p-4 sm:p-6">
                            <h2 className="text-lg sm:text-xl font-semibold" style={{ fontFamily: "Playfair Display" }}>
                                Keep Images & Restart Layout?
                            </h2>
                            <p className="mt-2 text-gray-600 text-sm sm:text-base" style={{ fontFamily: "Playfair Display" }}>
                                Your current layout design will be cleared, but all uploaded images will be kept.
                                You can modify your image collection and generate a new layout.
                            </p>
                            <div className="mt-4 flex flex-col sm:flex-row sm:justify-end gap-3 sm:gap-4">
                                <button
                                    className="w-full sm:w-auto px-4 py-2 bg-gray-200 rounded-md text-gray-700 hover:bg-gray-300 order-2 sm:order-1"
                                    onClick={closeSoftResetModal} 
                                    style={{ fontFamily: "Playfair Display" }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 order-1 sm:order-2"
                                    onClick={confirmSoftReset}
                                    style={{ fontFamily: "Playfair Display" }}
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Payment Modal - Make sure it's outside other containers for proper z-index handling */}
            {isPaymentModalOpen && (
                <PaymentModal
                    onClose={(result) => {
                        console.log("Closing payment modal", result);
                        setIsPaymentModalOpen(false);
                        // Return result to any component waiting for the modal to close
                        return result || { canceled: true };
                    }}
                    onProceed={handlePaymentProceed}
                />
            )}
            
            {/* Share modal */}
            {showModal && <ShareModal shareLink={shareLink} setShowModal={setShowModal} />}
        </QuiltBuilderWithLayout>
    );
}

export default QuiltBuilder;